import { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { Download } from 'lucide-react';
import { startOfWeek, endOfWeek, differenceInCalendarDays } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogFooter,
} from '@/components/ui/Dialog';
import { ResponsiveDialogContent } from '@/components/ui/ResponsiveDialog';
import {
  ResponsiveDialogHeader,
  ResponsiveDialogHelpButton,
} from '@/components/ui/ResponsiveDialogHeader';
import { DaySheetlyDate } from '@/components/fields/DaySheetlyDate';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { parseLocalDate, formatLocalDate, formatDisplayDate } from '@/utils/date';
import { exportJsonDialogQuery } from './ExportJsonDialogQuery';
import type { ExportJsonDialogQuery as ExportJsonDialogQueryType } from '@/__generated__/ExportJsonDialogQuery.graphql.ts';
import { captureEvent } from '@/analytics/posthog';

const MAX_RANGE_DAYS = 62;
const exportActionsClassName =
  '!flex-row gap-3 border-t bg-background px-4 py-4 sm:px-6 [&>button]:min-w-0 [&>button]:flex-1';

interface ExportProjectOption {
  id: string;
  name: string;
}

interface ExportMemberOption {
  id: string;
  name: string;
}

interface ExportJsonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projects: ExportProjectOption[];
  members: ExportMemberOption[];
  referenceDate: string;
  effectivePlan?: 'FREE' | 'PRO';
}

type ExportMode = 'BACKUP' | 'MIGRATION';

function getDefaultRange(referenceDate: string): { from: string; to: string } {
  const base = parseLocalDate(referenceDate);
  const from = formatLocalDate(startOfWeek(base, { weekStartsOn: 1 }));
  const to = formatLocalDate(endOfWeek(base, { weekStartsOn: 1 }));
  return { from, to };
}

function stripNullValues(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripNullValues);
  }

  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v !== null);
    return Object.fromEntries(entries.map(([k, v]) => [k, stripNullValues(v)]));
  }

  return value;
}

function ExportJsonDialogContent({
  from,
  to,
  selectedProjectIds,
  selectedMemberIds,
  onExport,
  onClose,
  isExporting,
  mode,
  effectivePlan,
}: {
  from: string;
  to: string;
  selectedProjectIds: string[];
  selectedMemberIds: string[];
  onExport: (data: ExportJsonDialogQueryType['response']) => void;
  onClose: () => void;
  isExporting: boolean;
  mode: ExportMode;
  effectivePlan: 'FREE' | 'PRO';
}) {
  const canMigrate = effectivePlan === 'PRO';
  const effectiveMode = mode === 'MIGRATION' && canMigrate ? 'MIGRATION' : 'BACKUP';
  const data = useLazyLoadQuery<ExportJsonDialogQueryType>(exportJsonDialogQuery, {
    from,
    to,
    projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : null,
    memberIds: selectedMemberIds.length > 0 ? selectedMemberIds : null,
    // Keep the query safe even if a stale client somehow has MIGRATION
    // selected while its billing snapshot is Free. The server remains the
    // final authority.
    mode: effectiveMode,
  });

  const count = data.exportTasks.tasks.length;
  const maxCount = 5000;

  return (
    <>
      <div className="space-y-4 px-4 pb-4 sm:px-6">
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <p>
            You are about to export{' '}
            <span className="font-semibold">{count}</span> task
            {count === 1 ? '' : 's'} between{' '}
            <span className="font-semibold">{formatDisplayDate(from)}</span> and{' '}
            <span className="font-semibold">{formatDisplayDate(to)}</span>.
          </p>
          {count === 0 && (
            <p className="mt-2 text-muted-foreground">No tasks match the selected filters.</p>
          )}
          {count > maxCount && (
            <p className="mt-2 text-destructive">
              Exports are limited to {maxCount} tasks. Please narrow the filters.
            </p>
          )}
          <p className="mt-2 text-muted-foreground">
            {effectiveMode === 'MIGRATION'
              ? 'This Pro migration bundle includes the referenced projects and members so it can be imported into another workspace.'
              : 'This Free backup includes the referenced projects and members so you can restore these tasks in this workspace.'}
          </p>
        </div>
      </div>

      <DialogFooter
        data-testid="export-json-actions"
        className={`sticky bottom-0 z-10 ${exportActionsClassName}`}
      >
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isExporting}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => onExport(data)}
          disabled={count === 0 || count > maxCount || isExporting}
          data-testid="export-json-submit"
          className="flex-1"
        >
          <Download className="mr-2 h-4 w-4" />
          Export {count > 0 ? count : ''} task{count === 1 ? '' : 's'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function ExportJsonDialog({
  isOpen,
  onClose,
  projects,
  members,
  referenceDate,
  effectivePlan = 'FREE',
}: ExportJsonDialogProps) {
  const defaultRange = useMemo(() => getDefaultRange(referenceDate), [referenceDate]);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [mode, setMode] = useState<ExportMode>('BACKUP');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (effectivePlan !== 'PRO') setMode('BACKUP');
  }, [effectivePlan]);

  const handleClose = useCallback(() => {
    setFrom(defaultRange.from);
    setTo(defaultRange.to);
    setSelectedProjectIds([]);
    setSelectedMemberIds([]);
    setMode('BACKUP');
    setIsExporting(false);
    onClose();
  }, [defaultRange, onClose]);

  const rangeDays = useMemo(() => {
    try {
      return differenceInCalendarDays(parseLocalDate(to), parseLocalDate(from));
    } catch {
      return null;
    }
  }, [from, to]);

  const isRangeValid =
    rangeDays !== null && rangeDays >= 0 && rangeDays <= MAX_RANGE_DAYS;

  const toggleProject = useCallback((id: string) => {
    setSelectedProjectIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }, []);

  const toggleMember = useCallback((id: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const handleExport = useCallback(
    (data: ExportJsonDialogQueryType['response']) => {
      setIsExporting(true);

      const cleaned = stripNullValues(data.exportTasks);
      const json = JSON.stringify(cleaned, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `daysheetly-tasks_${from}_to_${to}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      URL.revokeObjectURL(url);

      captureEvent('report_exported', {
        format: 'json',
        entry_count: data.exportTasks.tasks.length,
        export_mode: data.exportTasks.mode,
      });
      toast.success(`Exported ${data.exportTasks.tasks.length} task${data.exportTasks.tasks.length === 1 ? '' : 's'}`);
      setIsExporting(false);
      handleClose();
    },
    [from, to, handleClose],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <ResponsiveDialogContent data-testid="export-json-dialog" className="sm:!max-w-2xl">
        <ResponsiveDialogHeader
          title="Export JSON"
          description="Choose a date range and optionally filter by projects or members."
          onBack={handleClose}
          right={<ResponsiveDialogHelpButton label="Export help" />}
        />

        <div data-testid="export-json-body" className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-5 px-4 py-5 sm:px-6 sm:pb-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DaySheetlyDate
                mode="single"
                label="Start date"
                value={from || null}
                onChange={(date) => setFrom(date ?? '')}
                placeholder="Start date"
                clearable={false}
                testId="export-from-input"
              />
              <DaySheetlyDate
                mode="single"
                label="End date"
                value={to || null}
                onChange={(date) => setTo(date ?? '')}
                placeholder="End date"
                clearable={false}
                testId="export-to-input"
              />
            </div>

            {!isRangeValid && from && to && (
              <div className="text-sm text-destructive">
                {rangeDays !== null && rangeDays < 0
                  ? 'From must be before or equal to To.'
                  : `Date range must be at most ${MAX_RANGE_DAYS} days.`}
              </div>
            )}

            <div className="space-y-2 rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Export type</p>
                <p className="text-xs text-muted-foreground">
                  Backups stay in this workspace. Moving tasks to another workspace requires Pro.
                </p>
              </div>
              <Select value={mode} onValueChange={(value) => setMode(value as ExportMode)}>
                <SelectTrigger data-testid="export-mode-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BACKUP">Backup (Free)</SelectItem>
                  <SelectItem value="MIGRATION" disabled={effectivePlan !== 'PRO'}>
                    Migration (Pro)
                  </SelectItem>
                </SelectContent>
              </Select>
              {effectivePlan !== 'PRO' && (
                <p className="text-xs text-muted-foreground">
                  <a
                    href="/settings/subscription"
                    className="font-medium text-primary underline underline-offset-4"
                  >
                    Upgrade to Pro
                  </a>{' '}
                  to export a bundle for another workspace.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold">Filters</p>
                <p className="text-xs text-muted-foreground">Choose which projects and members to include.</p>
              </div>

              {projects.length > 0 && (
                <div className="rounded-lg border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-medium">Projects</p>
                    <span className="text-xs text-primary">
                      {selectedProjectIds.length || 'All'} selected
                    </span>
                  </div>
                  <div className="max-h-32 space-y-2 overflow-y-auto">
                    {projects.map((project) => (
                      <label key={project.id} className="flex min-h-9 items-center gap-2 text-sm">
                        <Checkbox
                          checked={selectedProjectIds.includes(project.id)}
                          onCheckedChange={() => toggleProject(project.id)}
                        />
                        {project.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {members.length > 0 && (
                <div className="rounded-lg border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-medium">Members</p>
                    <span className="text-xs text-primary">
                      {selectedMemberIds.length || 'All'} selected
                    </span>
                  </div>
                  <div className="max-h-32 space-y-2 overflow-y-auto">
                    {members.map((member) => (
                      <label key={member.id} className="flex min-h-9 items-center gap-2 text-sm">
                        <Checkbox
                          checked={selectedMemberIds.includes(member.id)}
                          onCheckedChange={() => toggleMember(member.id)}
                        />
                        {member.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isRangeValid ? (
            <Suspense
              fallback={
                <DialogFooter className={`sticky bottom-0 z-10 ${exportActionsClassName}`}>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isExporting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button disabled className="flex-1">
                    <Download className="mr-2 h-4 w-4" />
                    Loading…
                  </Button>
                </DialogFooter>
              }
            >
              <ExportJsonDialogContent
                from={from}
                to={to}
                selectedProjectIds={selectedProjectIds}
                selectedMemberIds={selectedMemberIds}
                onExport={handleExport}
                onClose={handleClose}
                isExporting={isExporting}
                mode={mode}
                effectivePlan={effectivePlan}
              />
            </Suspense>
          ) : (
            <DialogFooter className={`sticky bottom-0 z-10 ${exportActionsClassName}`}>
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button type="button" disabled data-testid="export-json-submit" className="flex-1">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DialogFooter>
          )}
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}
